import { useEffect, useRef, useState } from 'react';

import { Modal, type TypeSelectOption, type TypeTableRef, dialogService, toastService } from '@thefarmersfront/kitchen';
import { isDeepEqual } from '@thefarmersfront/kitchen-utensil';

import { useShallow } from 'zustand/react/shallow';

import { ModalContentsContainer } from '~/components/common';

import {
  useGetFilterAllQuery,
  usePostReceivingRequestMutation,
  usePutReceivingRequestConfirmMutation,
  useReceiveRequestRegisterTableRowValidation,
} from '~/hooks/receive';
import {
  DEFAULT_PICKUP_ADDRESS_DATA,
  DEFAULT_RECEIVE_REQUESTS_REGISTER_FILTER,
  DEFAULT_RECEIVE_REQUEST_ORDER_INFO_FILTER_ERROR_STATUS,
  useReceiveStore,
} from '~/store';
import {
  addIf,
  flattenRegisterErrorList as flattenErrorList,
  focusFirstErrorField,
  formatDate,
  renderFilterErrorMessage,
} from '~/utils';

import {
  DATE_FORMAT,
  DEFAULT_INPUT_STATUS,
  ERROR_REQUIRED_INPUT_STATUS,
  ERROR_REQUIRED_SELECT_STATUS,
  ERROR_VEHICLE_SIZE_REQUIRED_STATUS,
  PICKUP_SHIPPING_TYPES,
  RECEIVE,
} from '~/constants';

import { ReceiveRequestCaution } from '../ReceiveRequestCaution';
import { ReceiveRegisterFirstStepContent } from './ReceiveRegisterFirstStepContent';
import { ReceiveRegisterSecondStepContent } from './ReceiveRegisterSecondStepContent';

import type {
  TypePickupAddressDataState,
  TypePickupData,
  TypePostReceivingRequestParams,
  TypeReceiveRequestOrder,
  TypeReceiveRequestOrderInfoFilter,
  TypeReceiveRequestOrderInfoFilterErrorState,
} from '~/types/receive.types';

type TypeReceiveRequestRegisterModalProps = {
  open: boolean;
  ownerCode: string;
  temperatureOptionsByCurrentCenter: TypeSelectOption<string>[] | null;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * [입고관리 > 입고요청관리] 입고 요청 등록 모달
 */
const ReceiveRequestRegisterModal = ({
  open,
  ownerCode,
  temperatureOptionsByCurrentCenter,
  onCancel,
  onConfirm,
}: TypeReceiveRequestRegisterModalProps) => {
  const receivingRequestMutation = usePostReceivingRequestMutation();
  const receivingRequestConfirmMutation = usePutReceivingRequestConfirmMutation();
  const {
    errorList: rowErrorList,
    setErrorList: setRowErrorList,
    validateAllRows,
  } = useReceiveRequestRegisterTableRowValidation();

  const filterOptionsQuery = useGetFilterAllQuery('ADD_RECEIVING_GOODS', { showGlobalErrorToast: false });

  const [
    /** STEP 1 */
    requestModalDefaultInfoValue,
    resetRequestModalDefaultInfoValue,
    requestModalDefaultInfoStatus,
    setRequestModalDefaultInfoStatus,
    resetRequestModalDefaultInfoStatus,
    requestModalGoodsList,
    setRequestModalGoodsList,
    /** STEP 2 */
    requestModalOrderInfoValue,
    setRequestModalOrderInfoValue,
    setRequestModalOrderInfoStatus,
  ] = useReceiveStore(
    useShallow((state) => [
      state.requestModalDefaultInfoValue,
      state.resetRequestModalDefaultInfoValue,
      state.requestModalDefaultInfoStatus,
      state.setRequestModalDefaultInfoStatus,
      state.resetRequestModalDefaultInfoStatus,
      state.requestModalGoodsList,
      state.setRequestModalGoodsList,
      state.requestModalOrderInfoValue,
      state.setRequestModalOrderInfoValue,
      state.setRequestModalOrderInfoStatus,
    ])
  );

  useEffect(() => {
    if (filterOptionsQuery.error) {
      toastService.addToast({ type: 'error', persist: true, message: renderFilterErrorMessage() });
    }
  }, [filterOptionsQuery.error]);

  /** STEP 2 */
  const initialOrderInfoFilter = useRef<TypeReceiveRequestOrderInfoFilter[]>([]);

  const [requestOrderInfoData, setRequestOrderInfoData] = useState<TypeReceiveRequestOrder[]>([]);

  const [currentStep, setCurrentStep] = useState<1 | 2>(1);

  const [localTemperatureOptionsByCurrentCenter, setLocalTemperatureOptionsByCurrentCenter] = useState<
    TypeSelectOption<string>[]
  >(temperatureOptionsByCurrentCenter ?? filterOptionsQuery.data?.temperature ?? []);

  const tableRef = useRef<TypeTableRef>(null);

  /** STEP 2 에러 필드 DOM 참조 */
  const errorFieldRefs = useRef<Record<string, HTMLElement | null>>({});

  const registerReceiveRequest = () => {
    const { temperature, xDock, xDockCenter, supplierCode, note: remarks } = requestModalDefaultInfoValue;

    const formattedItemsInfo = requestModalGoodsList.map((item) => ({
      goodsId: item.goodsId,
      goodsCode: item.goodsCode,
      dateUseYn: item.dateUseYn,
      ...addIf(item.dateType === 'expirationDate', 'expirationDate', item.date),
      ...addIf(item.dateType === 'manufactureDate', 'manufactureDate', item.date),
      useByDate: item.dateType === 'expirationDate',
      usePallet: item.palletYn,
      ...addIf(item.lotNo !== '', 'lotNo', item.lotNo),
      boxQty: item.boxQty,
      boxPerQty: item.boxPerQty ?? 0,
      eachQty: item.eachQty,
    }));

    const params: TypePostReceivingRequestParams = {
      warehouse: temperature?.value ?? '',
      xdock: JSON.parse(xDock),
      ...addIf(xDockCenter?.value, 'xdockCenter'),
      supplierCode: supplierCode?.value ?? '',
      remarks,
      items: formattedItemsInfo,
    };

    receivingRequestMutation.mutate(
      { ownerCode, params },
      {
        onSuccess: ({ requestedOrders }) => {
          setRequestOrderInfoData(requestedOrders);

          const _initialOrderInfoFilter = requestedOrders.map(({ id }) => ({
            orderId: id,
            releaseCode: null,
            courier: '',
            invoiceNumber: '',
            expectedDate: null,
            expectedTime: null,
            kurlyPickupAddress: 'default' as const,
            milkrunPickupAddresses: {
              default: true,
              etc1: false,
              etc2: false,
            },
            pickupAddressData: {},
          }));

          setRequestModalOrderInfoValue(_initialOrderInfoFilter);
          initialOrderInfoFilter.current = _initialOrderInfoFilter;
          setRequestModalOrderInfoStatus(
            _initialOrderInfoFilter.map((order) => ({
              orderId: order.orderId,
              ...DEFAULT_RECEIVE_REQUEST_ORDER_INFO_FILTER_ERROR_STATUS,
            }))
          );

          setCurrentStep(2);
        },
      }
    );
  };

  /** 입고 요청 확정 API 호출 */
  const confirmReceiveRequest = () => {
    receivingRequestConfirmMutation.mutate(
      {
        ownerCode,
        warehouse: requestModalDefaultInfoValue.temperature?.value ?? '',
        orderInfoFilter: requestModalOrderInfoValue,
      },
      {
        onSuccess: () => {
          toastService.addToast({
            type: 'success',
            message: '입고 요청이 등록되었습니다.',
          });

          setRequestModalGoodsList([]);
          resetRequestModalDefaultInfoValue();
          resetRequestModalDefaultInfoStatus();
          setRequestModalOrderInfoValue([]);
          setRequestModalOrderInfoStatus([]);
          setRequestOrderInfoData([]);

          onConfirm();
        },
        onError: (error) => {
          const errorCode = error.code;

          if (
            errorCode === RECEIVE.RECEIVE_DATE_TIME_NOT_EXIST ||
            errorCode === RECEIVE.RECEIVE_TIME_DISABLED ||
            errorCode === RECEIVE.PICKUP_REGISTER_TIME_EXCEEDED
          ) {
            const errorOrderIds = error.values ?? [];

            setRequestModalOrderInfoStatus(
              requestModalOrderInfoValue.map((order) => {
                const isError = errorOrderIds.includes(order.orderId);
                return {
                  orderId: order.orderId,
                  ...DEFAULT_RECEIVE_REQUEST_ORDER_INFO_FILTER_ERROR_STATUS,
                  expectedDateTime: isError ? { status: 'error' } : DEFAULT_INPUT_STATUS,
                };
              })
            );
            return;
          }

          toastService.addToast({
            type: 'error',
            message: error.message || '입고 요청 등록에 실패했습니다.\n다시 시도해주세요.',
          });
        },
      }
    );
  };

  /** 모달 취소 처리 */
  const handleCancel = () => {
    const isModified =
      !isDeepEqual(DEFAULT_RECEIVE_REQUESTS_REGISTER_FILTER, requestModalDefaultInfoValue) ||
      requestModalGoodsList.length !== 0;

    if (!isModified) {
      setRequestModalGoodsList([]);
      resetRequestModalDefaultInfoValue();
      resetRequestModalDefaultInfoStatus();
      onCancel();
      return;
    }

    dialogService.addDialog({
      type: 'confirm',
      cancelText: '취소',
      confirmText: '나가기',
      children: `작성된 내용이 저장되지 않습니다.\n페이지를 나가시겠습니까?`,
      onConfirm: () => {
        setRequestModalGoodsList([]);
        resetRequestModalDefaultInfoValue();
        resetRequestModalDefaultInfoStatus();
        onCancel();
      },
    });
  };

  const handleFirstStepConfirm = () => {
    // 기본 정보 폼 유효성 검사
    const { center, temperature, supplierCode } = requestModalDefaultInfoValue;

    const updatedFilterStatus = {
      ...requestModalDefaultInfoStatus,
      ...(!center && { center: ERROR_REQUIRED_SELECT_STATUS }),
      ...(!temperature && { temperature: ERROR_REQUIRED_SELECT_STATUS }),
      ...(!supplierCode && { supplierCode: ERROR_REQUIRED_SELECT_STATUS }),
    };

    setRequestModalDefaultInfoStatus(updatedFilterStatus);

    const hasFilterError = Object.values(updatedFilterStatus).some(({ status }) => status === 'error');

    // 테이블 데이터 유효성 검사
    if (requestModalGoodsList.length === 0) {
      dialogService.addDialog({
        children: '입고할 상품을 1개 이상 추가해주세요.',
      });
      return;
    }

    const { errorList, firstErrorRowIndex: errorRowIndex } = validateAllRows();

    const hasTableError = errorList.some((error) => Object.values(error).includes('error'));

    if (hasTableError) {
      // 전체에서 에러가 2개 이상인 경우 스크롤
      const totalErrorCount = errorList.reduce((acc, error) => {
        const rowErrorCount = Object.values(error).filter((value) => value === 'error').length;
        return acc + rowErrorCount;
      }, 0);
      const hasMoreThanTwoErrorFields = totalErrorCount >= 2;

      if (hasMoreThanTwoErrorFields && errorRowIndex !== null) {
        tableRef.current?.scrollToIndex(errorRowIndex, { align: 'center' });
      }

      dialogService.addDialog({
        children: `입력 조건에 맞지 않는 상품 정보가 있습니다.\n다시 확인 후 시도해주세요.`,
      });

      return;
    }

    if (!hasFilterError) {
      registerReceiveRequest();
    }
  };

  /** 픽업 출고지 데이터 유효성 검사 */
  const validatePickupAddressData = (pickupData?: TypePickupData): TypePickupAddressDataState => {
    if (!pickupData) {
      return {
        address: ERROR_REQUIRED_INPUT_STATUS,
        palletQuantity: ERROR_REQUIRED_INPUT_STATUS,
        vehicleSize: DEFAULT_INPUT_STATUS,
      };
    }

    const isValidAddress = Boolean(pickupData.address);
    const isValidPalletQuantity = Boolean(pickupData.palletQuantity);
    const isValidVehicleSize = !pickupData.isUseVehicleSize || Boolean(pickupData.vehicleSize);

    return {
      address: isValidAddress ? DEFAULT_INPUT_STATUS : ERROR_REQUIRED_INPUT_STATUS,
      palletQuantity: isValidPalletQuantity ? DEFAULT_INPUT_STATUS : ERROR_REQUIRED_INPUT_STATUS,
      vehicleSize: isValidVehicleSize ? DEFAULT_INPUT_STATUS : ERROR_VEHICLE_SIZE_REQUIRED_STATUS,
    };
  };

  /** 입고 예정 정보 필수 필드 체크 */
  const checkRequiredField = (
    order: TypeReceiveRequestOrderInfoFilter
  ): TypeReceiveRequestOrderInfoFilterErrorState => {
    const isValidReleaseCode = Boolean(order.releaseCode);
    const isParcel = order.releaseCode?.value === 'PARCEL';
    const isValidExpectedDate = Boolean(order.expectedDate);
    const isValidExpectedTime = Boolean(isParcel || order.expectedTime);
    const isKurlyPickup = order.releaseCode?.value === PICKUP_SHIPPING_TYPES.KURLY_PICKUP;
    const isMilkrunPickup = order.releaseCode?.value === PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP;

    // 컬리픽업 검증
    let isValidKurlyPickupAddressSelection = true;

    const kurlyPickupAddressDataState: {
      kurlyDefault?: TypePickupAddressDataState;
      kurlyEtc?: TypePickupAddressDataState;
    } = {};

    if (isKurlyPickup) {
      // 선택 여부 확인
      isValidKurlyPickupAddressSelection = Boolean(order.kurlyPickupAddress);

      // 필수값 미입력 검증
      if (order.kurlyPickupAddress === 'default') {
        const validationResult = validatePickupAddressData(order.pickupAddressData?.kurlyDefault);
        kurlyPickupAddressDataState.kurlyDefault = validationResult;
      } else if (order.kurlyPickupAddress === 'etc') {
        const validationResult = validatePickupAddressData(order.pickupAddressData?.kurlyEtc);
        kurlyPickupAddressDataState.kurlyEtc = validationResult;
      }
    }

    // 밀크런픽업 검증
    let isValidMilkrunPickupAddressesSelection = true;

    const milkrunPickupAddressData: {
      milkrunDefault?: TypePickupAddressDataState;
      milkrunEtc1?: TypePickupAddressDataState;
      milkrunEtc2?: TypePickupAddressDataState;
    } = {};

    if (isMilkrunPickup) {
      // 선택 여부 확인
      const hasAnySelection =
        order.milkrunPickupAddresses.default || order.milkrunPickupAddresses.etc1 || order.milkrunPickupAddresses.etc2;

      isValidMilkrunPickupAddressesSelection = hasAnySelection;

      // 필수값 미입력 검증
      if (order.milkrunPickupAddresses.default) {
        const validationResult = validatePickupAddressData(order.pickupAddressData?.milkrunDefault);
        milkrunPickupAddressData.milkrunDefault = validationResult;
      }
      if (order.milkrunPickupAddresses.etc1) {
        const validationResult = validatePickupAddressData(order.pickupAddressData?.milkrunEtc1);
        milkrunPickupAddressData.milkrunEtc1 = validationResult;
      }
      if (order.milkrunPickupAddresses.etc2) {
        const validationResult = validatePickupAddressData(order.pickupAddressData?.milkrunEtc2);
        milkrunPickupAddressData.milkrunEtc2 = validationResult;
      }
    }

    return {
      orderId: order.orderId,
      releaseCode: isValidReleaseCode ? DEFAULT_INPUT_STATUS : ERROR_REQUIRED_SELECT_STATUS,
      expectedDateTime: DEFAULT_INPUT_STATUS,
      expectedDate: isValidExpectedDate ? DEFAULT_INPUT_STATUS : ERROR_REQUIRED_SELECT_STATUS,
      expectedTime: isValidExpectedTime ? DEFAULT_INPUT_STATUS : ERROR_REQUIRED_SELECT_STATUS,
      kurlyPickupAddress: isValidKurlyPickupAddressSelection ? DEFAULT_INPUT_STATUS : ERROR_REQUIRED_SELECT_STATUS,
      milkrunPickupAddresses: isValidMilkrunPickupAddressesSelection
        ? DEFAULT_INPUT_STATUS
        : ERROR_REQUIRED_SELECT_STATUS,
      pickupAddressData: {
        kurlyDefault: kurlyPickupAddressDataState.kurlyDefault || DEFAULT_PICKUP_ADDRESS_DATA,
        kurlyEtc: kurlyPickupAddressDataState.kurlyEtc || DEFAULT_PICKUP_ADDRESS_DATA,
        milkrunDefault: milkrunPickupAddressData.milkrunDefault || DEFAULT_PICKUP_ADDRESS_DATA,
        milkrunEtc1: milkrunPickupAddressData.milkrunEtc1 || DEFAULT_PICKUP_ADDRESS_DATA,
        milkrunEtc2: milkrunPickupAddressData.milkrunEtc2 || DEFAULT_PICKUP_ADDRESS_DATA,
      },
    };
  };

  /** 입고 예정 정보 폼 유효성 검사 */
  const validateOrderInfoForm = () => {
    const updatedFilterState = requestModalOrderInfoValue.map(checkRequiredField);

    setRequestModalOrderInfoStatus(updatedFilterState);

    const hasFilterError = updatedFilterState.some(
      (order) =>
        order.releaseCode.status === 'error' ||
        order.expectedDate.status === 'error' ||
        order.expectedTime.status === 'error' ||
        order.kurlyPickupAddress.status === 'error' ||
        order.milkrunPickupAddresses.status === 'error' ||
        hasPickupAddressDataError(order.pickupAddressData)
    );

    return { isValid: !hasFilterError, updatedFilterState };
  };

  const hasPickupAddressDataError = (
    pickupAddressDataState: TypeReceiveRequestOrderInfoFilterErrorState['pickupAddressData']
  ): boolean => {
    return Object.values(pickupAddressDataState).some(hasAddressDataFieldError);
  };

  const hasAddressDataFieldError = (addressDataState: TypePickupAddressDataState): boolean => {
    return Object.values(addressDataState).some((fieldState) => fieldState.status === 'error');
  };

  /** 입고 예정 정보 단계 confirm */
  const handleConfirm = () => {
    const { isValid: isValidForm, updatedFilterState } = validateOrderInfoForm();

    if (isValidForm) {
      dialogService.addDialog({
        type: 'confirm',
        cancelText: '취소',
        confirmText: '등록',
        children: '요청을 등록하시겠습니까?',
        onConfirm: confirmReceiveRequest,
      });
    } else {
      const errorStateMap = flattenErrorList(updatedFilterState);

      focusFirstErrorField(errorFieldRefs.current, errorStateMap);
    }
  };

  /** 모달 스텝 변경 처리 */
  const handleStepChange = (step: number) => {
    if (step === 2) {
      // validateForm 후, 입고 요청 API 호출, step 2 이동
      handleFirstStepConfirm();
    } else if (step === 1) {
      // Date 객체를 문자열 형식으로 변환하여 비교
      const normalizeForComparison = (data: TypeReceiveRequestOrderInfoFilter[]) =>
        data.map((item) => ({
          ...item,
          expectedDate: formatDate(item.expectedDate, DATE_FORMAT.DATE) ?? null,
        }));

      const isModified = !isDeepEqual(
        normalizeForComparison(initialOrderInfoFilter.current),
        normalizeForComparison(requestModalOrderInfoValue)
      );

      if (!isModified) {
        setCurrentStep(1);
      } else {
        dialogService.addDialog({
          type: 'confirm',
          cancelText: '취소',
          confirmText: '이전으로',
          children: `작성된 내용이 저장되지 않습니다.\n이전 페이지로 이동하시겠습니까?`,
          onConfirm: () => {
            setRequestModalOrderInfoValue([]);
            initialOrderInfoFilter.current = [];
            setRequestModalOrderInfoStatus([]);

            setRequestOrderInfoData([]);

            setCurrentStep(1);
          },
        });
      }
    }
  };

  return (
    <Modal
      title="입고 요청 등록"
      size="xlarge"
      confirmText="등록"
      open={open}
      step={{
        steps: [{ title: '기본 정보 / 입고 상품 입력' }, { title: '입고 예정 정보 입력' }],
        currentStep,
        onStepChange: handleStepChange,
      }}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
    >
      <ModalContentsContainer>
        <ReceiveRequestCaution />
        {currentStep === 1 && (
          <ReceiveRegisterFirstStepContent
            ownerCode={ownerCode}
            currentTemperatureOption={localTemperatureOptionsByCurrentCenter}
            setCurrentTemperatureOption={setLocalTemperatureOptionsByCurrentCenter}
            tableRef={tableRef}
            rowErrors={rowErrorList}
            setRowErrors={setRowErrorList}
          />
        )}
        {currentStep === 2 && (
          <ReceiveRegisterSecondStepContent
            ordersData={requestOrderInfoData}
            supplierCode={requestModalDefaultInfoValue.supplierCode?.value as string}
            errorFieldRefs={errorFieldRefs}
          />
        )}
      </ModalContentsContainer>
    </Modal>
  );
};

export default ReceiveRequestRegisterModal;
